// Line drawings for labelled-diagram questions ("Identify part B"). Each template is drawn in a
// 400 x 300 box; anchors are the points that a label's leader line touches. The part keys must
// match api/ai/_lib/figure_templates.json, which the server uses to validate the model's labels.

const outline = (width = 1.6) => `fill="none" stroke="#000" stroke-width="${width}"`;
const SOFT = 'fill="#f2f2f2" stroke="#000" stroke-width="1.4"';

export const FIGURE_TEMPLATES = {
    plant_cell: {
        title: 'Plant cell',
        body: `
            <rect x="60" y="30" width="280" height="240" rx="18" ${outline(3.2)}/>
            <rect x="69" y="39" width="262" height="222" rx="13" ${outline(1.2)}/>
            <rect x="120" y="92" width="182" height="132" rx="46" ${SOFT}/>
            <circle cx="112" cy="90" r="24" ${SOFT}/>
            <circle cx="117" cy="86" r="7" fill="#9a9a9a" stroke="#000" stroke-width="1"/>
            ${[[96, 205], [300, 72], [306, 244], [168, 247], [252, 60]].map(([x, y]) => `
                <ellipse cx="${x}" cy="${y}" rx="15" ry="7.5" fill="#d0d0d0" stroke="#000" stroke-width="1.2"/>
                <line x1="${x - 9}" y1="${y}" x2="${x + 9}" y2="${y}" stroke="#000" stroke-width="0.8"/>`).join('')}
        `,
        anchors: { cell_wall: [340, 130], cell_membrane: [69, 236], nucleus: [112, 90], cytoplasm: [318, 170], vacuole: [212, 160], chloroplast: [300, 72] }
    },
    animal_cell: {
        title: 'Animal cell',
        body: `
            <path d="M200 42 C285 38 352 88 350 152 C348 222 280 262 196 258 C112 254 50 214 52 150 C54 86 118 46 200 42 Z" ${outline(2.2)}/>
            <circle cx="176" cy="142" r="34" ${SOFT}/>
            <circle cx="182" cy="136" r="9" fill="#9a9a9a" stroke="#000" stroke-width="1"/>
            ${[[288, 112, -20], [122, 214, 15], [276, 214, 30]].map(([x, y, angle]) => `
                <g transform="rotate(${angle} ${x} ${y})">
                    <rect x="${x - 22}" y="${y - 10}" width="44" height="20" rx="10" fill="#e3e3e3" stroke="#000" stroke-width="1.3"/>
                    <polyline points="${x - 15},${y + 5} ${x - 9},${y - 5} ${x - 3},${y + 5} ${x + 3},${y - 5} ${x + 9},${y + 5} ${x + 15},${y - 5}" fill="none" stroke="#000" stroke-width="1"/>
                </g>`).join('')}
        `,
        anchors: { cell_membrane: [350, 152], nucleus: [176, 142], cytoplasm: [236, 176], mitochondrion: [288, 112] }
    },
    flower: {
        title: 'Flower (longitudinal section)',
        body: `
            <rect x="192" y="244" width="16" height="56" ${outline()}/>
            <path d="M170 232 C108 204 86 128 116 70 C148 108 164 172 180 228 Z" fill="#f7f7f7" stroke="#000" stroke-width="1.5"/>
            <path d="M230 232 C292 204 314 128 284 70 C252 108 236 172 220 228 Z" fill="#f7f7f7" stroke="#000" stroke-width="1.5"/>
            <path d="M168 242 C132 254 104 246 92 228 C122 230 148 234 170 236 Z" fill="#cfcfcf" stroke="#000" stroke-width="1.3"/>
            <path d="M232 242 C268 254 296 246 308 228 C278 230 252 234 230 236 Z" fill="#cfcfcf" stroke="#000" stroke-width="1.3"/>
            <ellipse cx="200" cy="238" rx="36" ry="13" fill="#e6e6e6" stroke="#000" stroke-width="1.5"/>
            <line x1="182" y1="228" x2="150" y2="124" stroke="#000" stroke-width="1.5"/>
            <line x1="218" y1="228" x2="250" y2="124" stroke="#000" stroke-width="1.5"/>
            <ellipse cx="148" cy="113" rx="7" ry="13" fill="#bdbdbd" stroke="#000" stroke-width="1.3"/>
            <ellipse cx="252" cy="113" rx="7" ry="13" fill="#bdbdbd" stroke="#000" stroke-width="1.3"/>
            <ellipse cx="200" cy="204" rx="22" ry="25" fill="#fff" stroke="#000" stroke-width="1.6"/>
            <circle cx="194" cy="198" r="4" fill="#9a9a9a" stroke="#000" stroke-width="0.8"/>
            <circle cx="206" cy="208" r="4" fill="#9a9a9a" stroke="#000" stroke-width="0.8"/>
            <line x1="200" y1="179" x2="200" y2="96" stroke="#000" stroke-width="1.8"/>
            <ellipse cx="200" cy="90" rx="11" ry="6" fill="#bdbdbd" stroke="#000" stroke-width="1.3"/>
        `,
        anchors: { petal: [120, 110], sepal: [104, 234], anther: [252, 113], filament: [236, 176], stigma: [200, 90], style: [200, 138], ovary: [212, 216], receptacle: [226, 244] }
    },
    plant: {
        title: 'Flowering plant',
        body: `
            <line x1="40" y1="230" x2="360" y2="230" stroke="#000" stroke-width="1.4" stroke-dasharray="6 4"/>
            <path d="M200 230 C198 180 202 130 200 82" ${outline(3)}/>
            <path d="M200 172 C170 150 128 152 112 168 C136 186 176 186 200 172 Z" fill="#dcdcdc" stroke="#000" stroke-width="1.5"/>
            <path d="M200 132 C230 110 272 112 288 128 C264 146 224 146 200 132 Z" fill="#dcdcdc" stroke="#000" stroke-width="1.5"/>
            ${[0, 60, 120, 180, 240, 300].map((angle) => `<ellipse cx="200" cy="48" rx="9" ry="18" transform="rotate(${angle} 200 62)" fill="#f7f7f7" stroke="#000" stroke-width="1.3"/>`).join('')}
            <circle cx="200" cy="62" r="9" fill="#bdbdbd" stroke="#000" stroke-width="1.3"/>
            <path d="M200 230 C196 252 180 268 160 282 M200 230 C202 256 204 272 200 292 M200 230 C206 250 222 266 244 278 M186 254 C176 258 168 256 160 262 M214 252 C224 256 232 254 240 260" ${outline(1.6)}/>
        `,
        anchors: { flower: [214, 50], leaf: [140, 168], stem: [200, 204], root: [181, 266] }
    },
    water_cycle: {
        title: 'Water cycle',
        body: `
            <defs><marker id="wc-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 Z" fill="#000"/></marker></defs>
            <circle cx="362" cy="38" r="17" fill="#e6e6e6" stroke="#000" stroke-width="1.4"/>
            ${[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => `<line x1="362" y1="14" x2="362" y2="7" transform="rotate(${angle} 362 38)" stroke="#000" stroke-width="1.4"/>`).join('')}
            <path d="M208 74 C202 54 230 44 242 56 C250 36 286 38 290 60 C310 56 322 74 310 86 L214 86 C200 86 198 76 208 74 Z" fill="#efefef" stroke="#000" stroke-width="1.5"/>
            <path d="M228 286 L300 150 L384 286 Z" fill="#e3e3e3" stroke="#000" stroke-width="1.5"/>
            <path d="M16 244 Q40 236 64 244 T112 244 T160 244 T208 244 L216 286 L16 286 Z" fill="#d6d6d6" stroke="#000" stroke-width="1.4"/>
            <path d="M96 232 C112 170 158 112 204 92" fill="none" stroke="#000" stroke-width="1.6" stroke-dasharray="6 4" marker-end="url(#wc-arrow)"/>
            ${[[234, 96], [252, 102], [270, 96], [288, 102]].map(([x, y]) => `<line x1="${x}" y1="${y}" x2="${x - 8}" y2="${y + 36}" stroke="#000" stroke-width="1.4" marker-end="url(#wc-arrow)"/>`).join('')}
            <path d="M262 214 C244 236 226 246 206 252" fill="none" stroke="#000" stroke-width="1.8" marker-end="url(#wc-arrow)"/>
        `,
        anchors: { evaporation: [138, 160], condensation: [256, 60], precipitation: [256, 118], runoff: [240, 236], collection: [70, 266] }
    }
};
